import React, { useEffect, useMemo, useState } from 'react';
import { formatAsCurrency } from '../../../utils/formatAsCurrency';
import { DocumentTextIcon } from '@heroicons/react/24/outline';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/solid';
import ShippingInformation from '../ShippingInformation';
import config from '../../../utils/config';
import NotFoundInventoryTable from '../NotFoundInventoryTable';
import ShippingMethod from '../ShippingMethod';
import { useNavigate } from 'react-router-dom';
import { GetShippingInfo, getOrderCSV } from '../../../utils/commonAPICalls';
import Notification from '../../../components/Notification';
import DocModal from '../DocModal';
import TermsAndConditionsBox from '../TermsAndConditionsBox';
import FeeTable from '../FeeTable';
import MorePartsModal from '../MorePartsModal';
import ExitingModal from '../ExitingModal';
import { validatePartsSearch } from '../../../utils/validators';
import AdvancedFilterDropdown from './AdvancedFilterDropdown';
import AdvancedOptionsDropdown from './AdvancedOptionsDropdown';
import AvailableInventorySortButton from './AvailableInventorySortButton';
import PendingPricingSection from './PendingPricingSection';
import logError from '../../../utils/errorUtil';

import { useAuth } from '../../context/auth-context';

import { calculateDarFee, calculateMinOrderFee, calculateOrderTotal } from '../../../utils/calculateFee';

function classNames(...classes) {
    return classes.filter(Boolean).join(' ');
}

export default function AvailableInventory({
    response,
    setResponse,
    noQuote,
    dashboardPartsSearch,
    setDashboardPartsSearch,
}) {
    const { currentUser, updateCurrentUser } = useAuth();

    let quoteId = response.quoteId;
    const [orderDetails, setOrderDetails] = useState(response);

    const [partsNotFound, setPartsNotFound] = useState(response.notFound);
    const [substituteParts, setSubstituteParts] = useState(response.substitutions);
    response = response.found;
    const ORIGIN = config.origin;
    const history = useNavigate();

    const [order, setOrder] = useState(response);
    const [originalOrders, setOriginalOrders] = useState([]);
    const [noResults, setNoResults] = useState(false);
    const [isShowing, setIsShowing] = useState(false);
    const [finalQuote, setFinalQuote] = useState(null);
    const [showShipping, setShowShipping] = useState(false);
    const [showPO, setShowPO] = useState(false);
    const [showSavedNotification, setShowSavedNotification] = useState(false);

    const [allStockSelected, setAllStockSelected] = useState(false);

    const [docs, setDocs] = useState();

    const [morePartsModal, setMorePartsModal] = useState(false);
    const [newPartInput, setNewPartInput] = useState('');
    const [newPartsSearchLoading, setNewPartsSearchLoading] = useState(false);
    const [showExitingModal, setShowExitingModal] = useState(false);

    const [showFeeTable, setShowFeeTable] = useState(false);
    const [showNoAogWarning, setShowNoAogWarning] = useState(null);

    //Sort states
    const [arrowDirections, setArrowDirections] = useState({
        partNo: { direction: null },
        condition: { direction: null },
        trace: { direction: null },
        tag: { direction: null },
        tagDate: { direction: null },
        traceLinks: { direction: null },
        leadTime: { direction: null },
        availableQty: { direction: null },
        unitPrice: { direction: null },
    });

    useEffect(() => {
        if (order.length === 0) return;

        setOriginalOrders((previousLines) => {
            if (previousLines.length === 0) return order;

            const nextLines = previousLines.slice();
            let addedLine = false;
            for (const line of order) {
                if (!nextLines.some((existingLine) => existingLine.quoteLineId === line.quoteLineId)) {
                    nextLines.push(line);
                    addedLine = true;
                }
            }
            return addedLine ? nextLines : previousLines;
        });
    }, [order]);

    const totalParts = useMemo(() => {
        const partNumbers = [];
        for (const line of originalOrders) {
            if (!partNumbers.includes(line.partNo)) partNumbers.push(line.partNo);
        }
        return partNumbers;
    }, [originalOrders]);

    const cartLines = order.filter((line) => !line.makeOffer);
    const cartAvailable = cartLines.length > 0;
    const arePartsSelected = cartLines.some((line) => line.selected);
    const leadTimeWarning = cartLines.some((line) => line.selected && line.leadTime !== 'Stock');
    const allCartLinesSelected = cartAvailable && cartLines.every((line) => line.selected);
    const totalDarFee = calculateDarFee(order, orderDetails.DARFee);
    const totalMinOrderFee = calculateMinOrderFee(
        order,
        orderDetails.minOrder,
        orderDetails.minAogOrder,
        orderDetails.isAOG
    );
    const orderSubtotal = calculateOrderTotal(order, orderDetails);
    const isDarOrder = order.some((line) => line.isDar);
    const advancedOptionSelection = {
        allSelected: allCartLinesSelected,
        allCartSelected: allCartLinesSelected,
        allStockSelected,
    };

    useEffect(() => {
        if (order.length === 0) {
            return setResponse();
        }
    }, [order]);

    useEffect(() => {
        setOrder(response);
    }, [response]);

    useEffect(() => {
        if (showSavedNotification) {
            setTimeout(() => {
                setShowSavedNotification(false);
            }, 3000);
        }
    }, [showSavedNotification]);

    useEffect(() => {
        if (dashboardPartsSearch === 'exiting') {
            setShowExitingModal(true);
        }
    }, [dashboardPartsSearch]);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);

        if (urlParams.get('editOrder') === '155638') {
            if (currentUser.orderDetails === null) {
                return history('/');
            }
            setOrderDetails(currentUser.orderDetails);
        }
    }, []);

    const handleOrderStatus = async (status) => {
        order.notFound = null;
        if (status === 'cancel') {
            setResponse();
            history('/');
        }
        if (status === 'submit') {
            saveQuote('submit');
            const data = GetShippingInfo(currentUser);
            data.then((data) => {
                updateCurrentUser({
                    ...currentUser,
                    shipAddresses: data.shipAddresses || [],
                    shipMethods: data.shipMethods || [],
                    availableShipMethods: data.availableShipMethods || [],
                });
                setIsShowing(true);
                window.scrollTo(0, document.body.scrollHeight);
            });
        }
        if (status === 'save') {
            //fetch request to save order
            await saveQuote('save');
        }
    };

    const saveQuote = async (action) => {
        const cartLines = order.filter((line) => !line.makeOffer);
        const dataToSend = {
            found: [...cartLines],
            contactId: currentUser.Id,
            accountId: currentUser.Account.Id,
            action: action,
            accountOwnerEmail: currentUser.Owner.Email,
            quoteNumber: finalQuote.quoteNumber,
            quoteId: finalQuote.quoteId,
            isAog: orderDetails.isAOG,
        };
        const response = await fetch(`${ORIGIN}/cp/save-quote`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                authorization: currentUser.tokenId,
            },
            body: JSON.stringify(dataToSend),
        });
        const data = await response.json();
        if (data.message) {
            return;
        }

        if (action !== 'submit') return setShowSavedNotification(true);
    };

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const quoteId = urlParams.get('quoteId');
        if (quoteId) {
            currentUser.finalQuote = {
                quoteId: orderDetails.quoteId,
                quoteNumber: orderDetails.quoteNumber,
            };
            setFinalQuote(orderDetails.quoteNumber);
        } else {
            fetchNewQuote();
        }
        if (currentUser.finalQuote) setFinalQuote(currentUser.finalQuote);
    }, []);

    const fetchNewQuote = async () => {
        const response = await fetch(`${ORIGIN}/cp/get-qn`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                authorization: currentUser.tokenId,
            },
            body: JSON.stringify({ tempId: quoteId }),
        });
        const data = await response.json();
        setFinalQuote(data);
    };

    const changeOrderQty = (partNo, qty, availableQty) => {
        if (qty === '-' || qty <= 0) {
            setOrder((prevState) =>
                prevState.map((line) => {
                    if (line.quoteLineId === partNo) {
                        return {
                            ...line,
                            quantityRequested: null,
                        };
                    }
                    return line;
                })
            );
        }
        if (qty <= availableQty && qty >= 0) {
            setOrder((prevState) =>
                prevState.map((line) => {
                    if (line.quoteLineId === partNo) {
                        return {
                            ...line,
                            quantityRequested: Number(qty, 10),
                        };
                    }
                    return line;
                })
            );
        }
    };
    const handleSelect = (partNo) => {
        const selectedLine = order.find((line) => line.quoteLineId === partNo);
        if (selectedLine && !selectedLine.selected) setShowFeeTable(true);

        setOrder((prevState) =>
            prevState.map((line) => {
                if (line.quoteLineId === partNo) {
                    return {
                        ...line,
                        selected: !line.selected,
                        isDar: false,
                    };
                }
                return line;
            })
        );
    };

    /////////////////////////////
    // Advanced Options Functions
    /////////////////////////////

    // updates the order with all rows selected
    const handleSelectAll = () => {
        const allSelected = cartLines.every((line) => line.selected);

        setOrder((prevState) =>
            prevState.map((line) => {
                if (line.makeOffer) return line;
                return {
                    ...line,
                    selected: !allSelected,
                };
            })
        );
    };

    const handleSelectAllCart = () => {
        const allSelected = allCartLinesSelected;

        setOrder((prevState) =>
            prevState.map((line) => {
                if (line.makeOffer) return line;
                return {
                    ...line,
                    selected: !allSelected,
                };
            })
        );

        setAllStockSelected(false);
    };

    const handleBuyAllAvailable = () => {
        if (!allCartLinesSelected) {
            handleSelectAllCart();
        }
        if (allStockSelected) {
            handleSelectAllCart();
        }
        setOrder((prevState) => {
            return prevState.map((line) => {
                if (!line.makeOffer) {
                    const newQty = allStockSelected ? 1 : line.availableQty;
                    changeOrderQty(line.quoteLineId, newQty, line.availableQty);
                    line.qty = newQty;
                }
                return line;
            });
        });
        setAllStockSelected(!allStockSelected);
    };

    const handleSubmitText = () => {
        const hasSelectedCartParts = order.some((line) => !line.makeOffer && line.selected);
        return hasSelectedCartParts ? 'Proceed to Cart' : '';
    };

    const handleSubmitOrder = () => {
        const dataToSend = {
            quoteNumber: finalQuote.quoteNumber,
            quoteId: finalQuote.quoteId,
            shipAddressId: currentUser.selectedShipAddress.shipAddressId,
            shipAddressName: currentUser.selectedShipAddress.shipAddressName,
            shipMethodId: currentUser.selectedShipMethod.shipMethodId,
            shipMethodName: currentUser.selectedShipMethod.shipMethodName,
        };

        fetch(`${ORIGIN}/cp/save-quote/ship-info`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                authorization: currentUser.tokenId,
            },
            body: JSON.stringify(dataToSend),
        })
            .then((response) => response.json())
            .then((data) => {
                let orderDefaults = order.map((item) => {
                    if (item.selected) {
                        item.originalCost = item.unitPrice;
                    }
                    return item;
                });
                setOrder(orderDefaults);
                currentUser.order = order;
            })
            .then((data) => {
                currentUser.order = order;
                currentUser.finalQuote = finalQuote;
                currentUser.orderDetails = {
                    ...orderDetails,
                    totalDarFee,
                    totalMinOrderFee,
                    orderSubtotal,
                };
                currentUser.surcharges = {
                    darFee: totalDarFee,
                    aogFee: orderDetails.isAOG ? orderDetails.AOGFee : '',
                    total: '',
                    isCreditCard: orderDetails.isCreditCard,
                };
                setDashboardPartsSearch(false);
                return history('/checkout');
            });
    };

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                const inputs = document.querySelectorAll('input[type="number"]');
                const index = Array.prototype.indexOf.call(inputs, e.target);
                if (index < inputs.length - 1) {
                    inputs[index + 1].focus();
                }
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                const inputs = document.querySelectorAll('input[type="number"]');
                const index = Array.prototype.indexOf.call(inputs, e.target);
                if (index > 0) {
                    inputs[index - 1].focus();
                }
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setNewPartsSearchLoading(true);

        const filteredData = validatePartsSearch(newPartInput);

        const dataToSend = {
            newParts: {
                parts: [...filteredData],
                contactId: currentUser.Id,
                accountId: currentUser.Account.Id,
            },
            currentParts: {
                found: [...order],
                contactId: currentUser.Id,
                accountId: currentUser.Account.Id,
                accountOwnerEmail: currentUser.Owner.Email,
                quoteNumber: finalQuote.quoteNumber,
                quoteId: finalQuote.quoteId,
            },
        };

        fetch(`${ORIGIN}/cp/add-parts`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                authorization: currentUser.tokenId,
            },
            body: JSON.stringify(dataToSend),
        })
            .then((response) => response.json())
            .then((data) => {
                setMorePartsModal(false);
                setNewPartsSearchLoading(false);
                setOrder(data.found);
                if (data?.notFound?.length > 0) {
                    setPartsNotFound(data.notFound);
                }
                if (data?.substitutions?.length > 0) {
                    setSubstituteParts(data.substitutions);
                }
            })
            .catch((error) => {
                logError(error, 'AvailableInventory.js');
            });
    };

    useEffect(() => {
        const hasSelectedParts = order.some((line) => line.selected);
        setShowFeeTable(hasSelectedParts);
    }, [order]);

    /////////////////////////////
    // Sorting Section
    /////////////////////////////

    const sortData = (field, direction) => {
        const sortedData = [...order];

        sortedData.sort((a, b) => {
            let aValue = a[field];
            let bValue = b[field];

            // Convert leadTime to number for sorting
            if (field === 'leadTime') {
                const convertLeadTime = (value) => {
                    const lowercasedValue = value.toLowerCase();
                    if (lowercasedValue === 'stock') return 0;

                    const numberValue = parseInt(lowercasedValue.split(' ')[0], 10);

                    if (isNaN(numberValue)) {
                        console.error(`Unexpected leadTime format: ${value}`);
                        return 9999; // Default high value for unexpected formats
                    }

                    return numberValue;
                };

                aValue = convertLeadTime(aValue);
                bValue = convertLeadTime(bValue);
            }

            if (field === 'tagDate') {
                aValue = new Date(aValue).getTime();
                bValue = new Date(bValue).getTime();
            }

            // Sorting logic for numbers
            if (typeof aValue === 'number' && typeof bValue === 'number') {
                if (direction === 'asc') {
                    return aValue - bValue;
                } else {
                    return bValue - aValue;
                }
            }
            // Sorting logic for strings
            else {
                if (direction === 'asc') {
                    if (aValue < bValue) return -1;
                    if (aValue > bValue) return 1;
                    return 0;
                } else {
                    if (aValue > bValue) return -1;
                    if (aValue < bValue) return 1;
                    return 0;
                }
            }
        });

        setOrder(sortedData);
    };

    const pendingPricingParts = originalOrders.filter((line) => line.makeOffer);

    return (
        <>
            <TermsAndConditionsBox />
            {docs && <DocModal setDocs={setDocs} docs={docs} />}
            <div className='mx-auto w-full transition-all max-w-10xl'>
                <div className='flex justify-between items-end'>
                    <div className='flex space-x-2'>
                        <div className='flex flex-col-reverse gap-2'>
                            <h1 className='text-3xl font-bold tracking-tight leading-tight text-gray-900'>
                                Available Inventory
                            </h1>
                            {noQuote ? null : finalQuote ? (
                                <h1 className='text-xl tracking-tight leading-tight text-gray-900 transition-all'>
                                    {finalQuote.quoteNumber}
                                </h1>
                            ) : (
                                <div className='flex justify-center items-center mt-2 w-36 h-8 bg-gray-100 rounded-md animate-pulse'>
                                    <p className='text-sm text-gray-500'>loading quote</p>
                                </div>
                            )}
                        </div>
                        <div className='flex items-end m-1 w-32 rounded-md md:w-auto'>
                            <AdvancedFilterDropdown
                                originalOrders={originalOrders}
                                order={order}
                                setOrder={setOrder}
                                setNoResults={setNoResults}
                                totalParts={totalParts}
                            />
                        </div>
                    </div>

                    <div className='flex flex-col justify-end space-x-4 md:flex-row'>
                        <div
                            onClick={() => {
                                getOrderCSV(currentUser, order, finalQuote.quoteNumber);
                            }}
                        >
                            <button
                                disabled={!finalQuote}
                                className='py-2 mt-4 ml-auto w-24 font-bold bg-white rounded border cursor-pointer hover:bg-blue-primary hover:text-white text-blue-primary border-blue-primary md:px-4 md:w-auto'
                            >
                                Download CSV
                            </button>
                        </div>
                        <div>
                            <button
                                onClick={() => {
                                    setMorePartsModal(true);
                                }}
                                disabled={!finalQuote}
                                className='py-2 mt-4 ml-auto w-24 font-bold bg-white rounded border cursor-pointer hover:bg-blue-primary hover:text-white text-blue-primary border-blue-primary md:px-4 md:w-auto'
                            >
                                Add more parts
                            </button>
                        </div>
                    </div>
                </div>

                <div className='mt-4'>
                    <table>
                        <thead className='sticky top-0 z-10'>
                            <tr className='text-white bg-blue-primary'>
                                <th scope='col' className='relative px-3 py-2 w-36 font-normal'>
                                    <div style={{ top: '8px', left: '10px' }} className='absolute'>
                                        Part Number
                                    </div>
                                    <div style={{ bottom: '10px', right: '10px' }} className='absolute'>
                                        <AvailableInventorySortButton
                                            arrowDirections={arrowDirections}
                                            setArrowDirections={setArrowDirections}
                                            name='partNo'
                                            sortData={sortData}
                                        />
                                    </div>
                                </th>
                                <th scope='col' className='relative px-6 py-2 font-normal'>
                                    <div className='flex flex-col justify-between h-full'>
                                        <div style={{ top: '8px', left: '10px' }} className='absolute'>
                                            Description
                                        </div>
                                    </div>
                                </th>
                                <th scope='col' className='relative px-3 py-2 w-36 font-normal'>
                                    <div className='flex flex-col justify-between h-full'>
                                        <div style={{ top: '8px', left: '10px' }} className='absolute'>
                                            Condition
                                        </div>
                                        <div style={{ bottom: '10px', right: '10px' }} className='absolute'>
                                            <AvailableInventorySortButton
                                                arrowDirections={arrowDirections}
                                                setArrowDirections={setArrowDirections}
                                                name='condition'
                                                sortData={sortData}
                                            />
                                        </div>
                                    </div>
                                </th>
                                <th scope='col' className='relative px-3 py-4 font-normal'>
                                    <div className='flex flex-col justify-between h-full'>
                                        <div style={{ top: '8px', left: '10px' }} className='absolute'>
                                            Trace
                                        </div>
                                        <div style={{ bottom: '10px', right: '10px' }} className='absolute'>
                                            <AvailableInventorySortButton
                                                arrowDirections={arrowDirections}
                                                setArrowDirections={setArrowDirections}
                                                name='trace'
                                                sortData={sortData}
                                            />
                                        </div>
                                    </div>
                                </th>
                                <th
                                    scope='col'
                                    className='relative px-3 py-4 w-[6rem] min-w-[6rem] max-w-[6rem] font-normal'
                                >
                                    <div className='flex flex-col justify-between h-full'>
                                        <div style={{ top: '8px', left: '10px' }} className='absolute'>
                                            Tag
                                        </div>
                                        <div style={{ bottom: '10px', right: '10px' }} className='absolute'>
                                            <AvailableInventorySortButton
                                                arrowDirections={arrowDirections}
                                                setArrowDirections={setArrowDirections}
                                                name='tag'
                                                sortData={sortData}
                                            />
                                        </div>
                                    </div>
                                </th>
                                <th scope='col' className='relative px-3 py-4 w-36 font-normal'>
                                    <div className='flex flex-col justify-between h-full'>
                                        <div style={{ top: '8px', left: '10px' }} className='absolute'>
                                            Tag Date
                                        </div>
                                        <div style={{ bottom: '10px', right: '10px' }} className='absolute'>
                                            <AvailableInventorySortButton
                                                arrowDirections={arrowDirections}
                                                setArrowDirections={setArrowDirections}
                                                name='tagDate'
                                                sortData={sortData}
                                            />
                                        </div>
                                    </div>
                                </th>
                                <th scope='col' className='relative px-3 py-4 w-24 font-normal'>
                                    <div className='flex flex-col justify-between h-full'>
                                        <div style={{ top: '8px', left: '10px' }} className='absolute'>
                                            Docs
                                        </div>
                                    </div>
                                </th>
                                <th scope='col' className='relative px-3 py-4 w-36 font-normal'>
                                    <div className='flex flex-col justify-between h-full'>
                                        <div style={{ top: '8px', left: '10px' }} className='absolute'>
                                            Lead Time
                                        </div>
                                        <div style={{ bottom: '10px', right: '10px' }} className='absolute'>
                                            <AvailableInventorySortButton
                                                arrowDirections={arrowDirections}
                                                setArrowDirections={setArrowDirections}
                                                name='leadTime'
                                                sortData={sortData}
                                            />
                                        </div>
                                    </div>
                                </th>
                                <th scope='col' className='relative px-3 py-4 w-40 font-normal'>
                                    <div className='flex flex-col justify-between h-full'>
                                        <div style={{ top: '8px', left: '10px' }} className='absolute mr-2'>
                                            Available Qty
                                        </div>
                                        <div style={{ bottom: '10px', right: '10px' }} className='absolute'>
                                            <AvailableInventorySortButton
                                                arrowDirections={arrowDirections}
                                                setArrowDirections={setArrowDirections}
                                                name='availableQty'
                                                sortData={sortData}
                                            />
                                        </div>
                                    </div>
                                </th>
                                <th scope='col' className='relative px-7 py-4 w-36 font-normal text-center'>
                                    <div className='flex flex-col justify-between h-full'>
                                        <div style={{ top: '8px', left: '10px' }} className='absolute'>
                                            Qty to Order
                                        </div>
                                    </div>
                                </th>
                                <th scope='col' className='relative px-3 py-4 w-28 font-normal'>
                                    <div className='flex flex-col justify-between h-full'>
                                        <div style={{ top: '8px', left: '10px' }} className='absolute'>
                                            Price Per Unit
                                        </div>
                                        <div style={{ bottom: '10px', right: '10px' }} className='absolute'>
                                            <AvailableInventorySortButton
                                                arrowDirections={arrowDirections}
                                                setArrowDirections={setArrowDirections}
                                                name='unitPrice'
                                                sortData={sortData}
                                            />
                                        </div>
                                    </div>
                                </th>
                                <th scope='col' className='relative px-3 py-4 w-28 font-normal text-center'>
                                    <div className='flex flex-col justify-between h-full'>
                                        <div style={{ top: '8px', left: '10px' }} className='absolute'>
                                            Request for DAR
                                        </div>
                                    </div>
                                </th>
                                <th
                                    scope='col'
                                    className='relative px-1 py-4 font-normal text-center align-middle whitespace-nowrap'
                                >
                                    <AdvancedOptionsDropdown
                                        handleSelectAll={handleSelectAll}
                                        handleSelectAllCart={handleSelectAllCart}
                                        handleBuyAllAvailable={handleBuyAllAvailable}
                                        advancedOptionSelection={advancedOptionSelection}
                                        cartAvailable={cartAvailable}
                                    />
                                </th>

                                <th scope='col' className='relative px-3 py-4 w-36 font-normal'>
                                    <div className='flex flex-col justify-between h-full'>
                                        <div style={{ top: '8px', left: '10px' }} className='absolute'>
                                            Total Cost
                                        </div>
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {noResults ? (
                                // If no results from filters, display the "No results found" message
                                <tr>
                                    <td
                                        colSpan='14'
                                        className='px-4 py-2 border text-centered'
                                        style={{ textAlign: 'center' }}
                                    >
                                        <p className='px-2 text-xl font-bold text-red-500'>No results found.</p>
                                        <p className='px-2 text-xs text-red-500'>
                                            Either no results found, or parts do not match filters applied.
                                        </p>
                                    </td>
                                </tr>
                            ) : cartLines.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan='14'
                                        className='px-4 py-2 border text-centered'
                                        style={{ textAlign: 'center' }}
                                    >
                                        <p className='px-2 text-sm text-gray-500'>
                                            No priced inventory available for purchase.
                                        </p>
                                    </td>
                                </tr>
                            ) : (
                                cartLines.map((item) => (
                                    <tr key={item.quoteLineId} className='h-20 border-b border-gray-200 hover:bg-gray-100'>
                                        <td className='px-4 py-2 font-semibold text-center border'>
                                            {item.partNo}
                                            {item.originalPartNumber && (
                                                <>
                                                    <QuestionMarkCircleIcon
                                                        onMouseEnter={() => {
                                                            setOrder(
                                                                order.map((i) => {
                                                                    if (i.quoteLineId === item.quoteLineId) {
                                                                        return {
                                                                            ...i,
                                                                            showOriginalPartToolTip: true,
                                                                        };
                                                                    }
                                                                    return i;
                                                                })
                                                            );
                                                        }}
                                                        onMouseLeave={() => {
                                                            setOrder(
                                                                order.map((i) => {
                                                                    if (i.quoteLineId === item.quoteLineId) {
                                                                        return {
                                                                            ...i,
                                                                            showOriginalPartToolTip: false,
                                                                        };
                                                                    }
                                                                    return i;
                                                                })
                                                            );
                                                        }}
                                                        className='inline float-right mt-0.5 mr-2 w-5 h-5 cursor-pointer text-blue-primary'
                                                    />
                                                    {item.showOriginalPartToolTip && (
                                                        <div className='absolute z-10 p-2 w-36 bg-gray-50 rounded-md shadow-lg'>
                                                            <p className='text-xs text-gray-800'>
                                                                This is a substitute part for the part you originally
                                                                requested original part: ({item.originalPartNumber})
                                                            </p>
                                                        </div>
                                                    )}
                                                    <p className='text-xs text-gray-500'>
                                                        original part: ({item.originalPartNumber})
                                                    </p>
                                                </>
                                            )}
                                        </td>
                                        <td
                                            className={`px-4 py-2 text-center border ${
                                                item.consignment ? 'text-blue-primary' : ''
                                            }`}
                                        >
                                            {item.description}{' '}
                                            {item.consignment
                                                ? `***Customer Consignment Inventory${
                                                      item.consignmentOwnerCodeName
                                                          ? ' - ' + item.consignmentOwnerCodeName
                                                          : ''
                                                  }***`
                                                : ''}
                                        </td>
                                        <td className='px-4 py-2 text-center border'>{item.condition}</td>
                                        <td className='px-4 py-2 border'>
                                            <div className='relative'>
                                                <div className='p-2 text-left'>{item.trace}</div>
                                                <div className='absolute -top-1 -right-3'>
                                                    {item.traceSources.length > 0 && (
                                                        <QuestionMarkCircleIcon
                                                            className='w-5 h-5 text-gray-400'
                                                            onMouseEnter={() => {
                                                                setOrder(
                                                                    order.map((i) => {
                                                                        if (i.quoteLineId === item.quoteLineId) {
                                                                            return {
                                                                                ...i,
                                                                                showTraceModal: true,
                                                                            };
                                                                        }
                                                                        return i;
                                                                    })
                                                                );
                                                            }}
                                                            onMouseLeave={() => {
                                                                setOrder(
                                                                    order.map((i) => {
                                                                        if (i.quoteLineId === item.quoteLineId) {
                                                                            return {
                                                                                ...i,
                                                                                showTraceModal: false,
                                                                            };
                                                                        }
                                                                        return i;
                                                                    })
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                    {item.showTraceModal && <SourceModal traceSources={item.traceSources} />}
                                                </div>
                                            </div>
                                        </td>
                                        <td className='w-0 px-4 py-2 border'>
                                            <div className='relative w-[6rem] min-w-[6rem] max-w-[6rem]'>
                                                <div className='py-2 text-left whitespace-normal'>{item.tag}</div>
                                                {item.tagAgencies.length > 0 && (
                                                    <div className='absolute -top-1 -right-3'>
                                                        <QuestionMarkCircleIcon
                                                            className='w-5 h-5 text-gray-400'
                                                            onMouseEnter={() => {
                                                                setOrder(
                                                                    order.map((i) => {
                                                                        if (i.quoteLineId === item.quoteLineId) {
                                                                            return {
                                                                                ...i,
                                                                                showTagModal: true,
                                                                            };
                                                                        }
                                                                        return i;
                                                                    })
                                                                );
                                                            }}
                                                            onMouseLeave={() => {
                                                                setOrder(
                                                                    order.map((i) => {
                                                                        if (i.quoteLineId === item.quoteLineId) {
                                                                            return {
                                                                                ...i,
                                                                                showTagModal: false,
                                                                            };
                                                                        }
                                                                        return i;
                                                                    })
                                                                );
                                                            }}
                                                        />
                                                        {item.showTagModal && (
                                                            <SourceModal traceSources={item.tagAgencies} />
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td className='px-4 py-2 text-center border'>
                                            {item.tagDate ? item.tagDate.slice(0, 10) : null}
                                        </td>
                                        <td
                                            className='px-4 py-2 mr-2 border cursor-pointer'
                                            onClick={() => {
                                                setDocs(item);
                                            }}
                                        >
                                            {item.traceLinks.length > 0 && (
                                                <DocumentTextIcon className='inline float-right mt-0.5 mr-2 w-5 h-5 text-gray-700 cursor-pointer' />
                                            )}
                                        </td>
                                        <td className='px-4 py-2 text-center border'>{item.leadTime}</td>
                                        <td className='px-4 py-2 text-center border'>{item.availableQty}</td>
                                        <td className='px-2 py-1 border'>
                                            <input
                                                type='number'
                                                min='0'
                                                disabled={isShowing}
                                                className='w-full border border-gray-300 shadow-sm'
                                                defaultValue={1}
                                                value={item.quantityRequested || ''}
                                                onKeyPress={(event) => {
                                                    if (!/[0-9]/.test(event.key)) {
                                                        event.preventDefault();
                                                    }
                                                }}
                                                onChange={(e) =>
                                                    changeOrderQty(item.quoteLineId, e.target.value, item.availableQty)
                                                }
                                                onBlur={(e) => {
                                                    if (e.target.value < 1) {
                                                        e.target.value = 1;
                                                        changeOrderQty(item.quoteLineId, e.target.value, item.availableQty);
                                                    }
                                                }}
                                            />
                                        </td>
                                        <td className='px-4 py-2 border'>{formatAsCurrency(item.unitPrice)}</td>
                                        <td
                                            className='px-4 py-2 border'
                                            onMouseLeave={() => {
                                                setOrder(
                                                    order.map((i) => {
                                                        if (i.quoteLineId === item.quoteLineId) {
                                                            return {
                                                                ...i,
                                                                showDarWarning: false,
                                                            };
                                                        }
                                                        return i;
                                                    })
                                                );
                                            }}
                                        >
                                            <div
                                                onMouseEnter={() => {
                                                    const darInput = document.getElementById('dar');
                                                    if (!darInput.disabled) {
                                                        return;
                                                    }

                                                    setOrder(
                                                        order.map((i) => {
                                                            if (i.quoteLineId === item.quoteLineId) {
                                                                return {
                                                                    ...i,
                                                                    showDarWarning: true,
                                                                };
                                                            } else {
                                                                return {
                                                                    ...i,
                                                                    showDarWarning: false,
                                                                };
                                                            }
                                                        })
                                                    );
                                                }}
                                            >
                                                {item.condition === 'NE' ? (
                                                    <div className='w-20 center'>
                                                        <div className='flex align-items-center'>
                                                            <div className='mr-2 text-center'>
                                                                <p>{item.isDar ? 'Remove DAR' : 'Add DAR'}</p>
                                                            </div>

                                                            <input
                                                                type='checkbox'
                                                                id='dar'
                                                                disabled={orderDetails.isAOG || isShowing}
                                                                className='flex-shrink-0 w-5 h-5 disabled:cursor-pointer'
                                                                checked={item.isDar}
                                                                onChange={(e) => {
                                                                    setOrder((prevState) => {
                                                                        return prevState.map((i) => {
                                                                            if (i.quoteLineId === item.quoteLineId) {
                                                                                return {
                                                                                    ...i,
                                                                                    isDar: e.target.checked,
                                                                                    selected: true,
                                                                                };
                                                                            }
                                                                            return i;
                                                                        });
                                                                    });
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                ) : null}
                                            </div>
                                            {item.showDarWarning && (
                                                <div className='absolute z-10 p-2 w-36 bg-white rounded-md border shadow-lg'>
                                                    <p className='text-xs text-gray-800'>
                                                        <p className='text-xs text-gray-800'>Order cannot be AOG and DAR</p>
                                                    </p>
                                                </div>
                                            )}
                                        </td>
                                        <td className='px-1 py-2 text-center border'>
                                            <ToggleButton
                                                isSelected={item.selected}
                                                partNo={item.quoteLineId}
                                                handleSelect={handleSelect}
                                                isShowing={isShowing}
                                            />
                                        </td>
                                        <td className='px-4 py-2 text-center border'>
                                            <p
                                                className={classNames(
                                                    item.selected ? 'text-blue-primary font-semibold' : '',
                                                    'text-gray-400'
                                                )}
                                            >
                                                {formatAsCurrency(item.unitPrice * item.quantityRequested)}
                                            </p>
                                        </td>
                                    </tr>
                                ))
                            )}
                            <tr>
                                <td colSpan='14' className='px-4 py-2 border'>
                                    <p className='px-2 text-sm text-gray-400'>
                                        *Orders may be subject to minimum order fees. Which include ${orderDetails.DARFee}{' '}
                                        for DAR and ${orderDetails.AOGFee} for AOG. Sales Tax and credit card or wire fees
                                        listed as PROCESSING FEE may be applied where applicable.
                                    </p>
                                    <p className='px-2 text-sm text-gray-400'>
                                        **This amount will be evenly distributed to all your parts on the order confirmation
                                        page.
                                    </p>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <FeeTable
                        order={order}
                        orderDetails={{ ...orderDetails, totalDarFee, totalMinOrderFee }}
                        isDarOrder={isDarOrder}
                        orderSubtotal={orderSubtotal}
                        isShowing={showFeeTable}
                    />

                    {!noQuote && cartAvailable && (
                        <div className='flex justify-end py-4 ml-auto space-x-12 w-1/3'>
                            <div
                                onMouseEnter={() => {
                                    setShowNoAogWarning(true);
                                }}
                                onMouseLeave={() => {
                                    setShowNoAogWarning(false);
                                }}
                            >
                                <button
                                    type='checkbox'
                                    disabled={isShowing || leadTimeWarning}
                                    onClick={() => {
                                        setOrderDetails((prevState) => {
                                            return {
                                                ...prevState,
                                                isAOG: !prevState.isAOG,
                                                totalDarFee: 0,
                                            };
                                        });
                                        setOrder((prevState) => {
                                            return prevState.map((i) => {
                                                return {
                                                    ...i,
                                                    isDar: false,
                                                };
                                            });
                                        });
                                    }}
                                    id='aog-order'
                                    className={classNames(
                                        orderDetails.isAOG ? 'bg-blue-primary text-white' : 'bg-gray-300 text-gray-900',
                                        'font-semibold py-4 px-5  rounded   h-14 -mt-0 cursor-pointer'
                                    )}
                                >
                                    {orderDetails.isAOG ? <span className=''>Cancel AOG</span> : 'AOG'}
                                </button>
                                {showNoAogWarning && leadTimeWarning && (
                                    <div className='absolute z-50 p-2 m-2 w-36 bg-gray-100 rounded-md border shadow-lg'>
                                        <p className='text-xs text-gray-800'>All part Lead Times must be stock</p>
                                    </div>
                                )}
                            </div>

                            {!isShowing && (
                                <button
                                    disabled={!arePartsSelected || !finalQuote}
                                    className='flex justify-center items-center px-5 py-4 text-lg font-bold text-white rounded border cursor-pointer hover:text-blue-primary hover:bg-white border-blue-primary bg-blue-primary'
                                    onClick={() => {
                                        handleOrderStatus('submit');
                                    }}
                                >
                                    {!finalQuote && (
                                        <>
                                            <svg
                                                role='status'
                                                className='inline mr-3 w-4 h-4 text-white animate-spin'
                                                viewBox='0 0 100 101'
                                                fill='none'
                                                xmlns='http://www.w3.org/2000/svg'
                                            >
                                                <path
                                                    d='M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z'
                                                    fill='#E5E7EB'
                                                />
                                                <path
                                                    d='M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z'
                                                    fill='currentColor'
                                                />
                                            </svg>
                                            Loading...
                                        </>
                                    )}
                                    {!arePartsSelected && finalQuote && 'Select Parts'}
                                    {finalQuote && handleSubmitText()}
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {isShowing && (
                    <ShippingInformation
                        isShowing={isShowing}
                        setIsShowing={setIsShowing}
                        setShowShipping={setShowShipping}
                        showShipping={showShipping}
                    />
                )}

                {showShipping && (
                    <ShippingMethod setShowPO={setShowPO} showPO={showPO} handleSubmitOrder={handleSubmitOrder} />
                )}
            </div>
            {!isShowing &&
                (pendingPricingParts.length > 0 ||
                    (substituteParts && substituteParts.length > 0) ||
                    partsNotFound.length > 0) && (
                    <div className='mx-auto mt-4 w-full max-w-10xl space-y-12'>
                        {pendingPricingParts.length > 0 && <PendingPricingSection parts={pendingPricingParts} />}
                        {substituteParts && substituteParts.length > 0 && (
                            <NotFoundInventoryTable data={substituteParts} title={'Alternate Parts'} />
                        )}
                        {partsNotFound.length > 0 && (
                            <NotFoundInventoryTable data={partsNotFound} title={'Searched parts currently not in stock'} />
                        )}
                    </div>
                )}
            <Notification
                setShowSavedNotification={setShowSavedNotification}
                showSavedNotification={showSavedNotification}
                title={'Your Quote has been successfully saved.'}
                desc={'To view your saved quotes, click on the `Quotes` tab in the navigation above.'}
                status={'success'}
            />
            {morePartsModal && (
                <MorePartsModal
                    handleSubmit={handleSubmit}
                    openEditModal={morePartsModal}
                    setOpenEditModal={setMorePartsModal}
                    setNewPartInput={setNewPartInput}
                    isLoading={newPartsSearchLoading}
                />
            )}

            <ExitingModal
                isShowing={showExitingModal}
                setIsShowing={setShowExitingModal}
                setDashboardPartsSearch={setDashboardPartsSearch}
                setMorePartsModal={setMorePartsModal}
            />
        </>
    );
}

const ToggleButton = ({ partNo, handleSelect, isSelected, isShowing }) => {
    return (
        <div className='flex justify-center items-start !mt-0'>
            <button
                disabled={isShowing}
                className={classNames(
                    !isSelected ? 'text-white bg-blue-primary' : 'text-gray-900 bg-gray-300',
                    'inline-grid px-3 py-2 font-semibold leading-tight text-center rounded-sm cursor-pointer'
                )}
                onClick={() => handleSelect(partNo)}
            >
                <span aria-hidden className='invisible col-start-1 row-start-1 whitespace-nowrap'>
                    Add to Cart
                </span>
                <span className='col-start-1 row-start-1 w-0 min-w-full whitespace-normal'>
                    {!isSelected ? 'Add to Cart' : 'Remove from Cart'}
                </span>
            </button>
        </div>
    );
};

const SourceModal = ({ traceSources }) => {
    return (
        <>
            <div className='absolute z-10 p-2 bg-white rounded-md border shadow-lg'>
                <p className='text-xs text-gray-800'>
                    {traceSources.map((item) => {
                        return <p className='w-max text-xs text-gray-800'>{item}</p>;
                    })}
                </p>
            </div>
        </>
    );
};
